import { motion } from "framer-motion";
import { Car, PlaneTakeoff, Train, MapPinned, ArrowRight, Route, Briefcase, HeartHandshake, Bus, Crown, Compass } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const SERVICES = [
  { title: "Local Taxi Service", desc: "Point-to-point city rides across Jodhpur — hourly & full-day.", icon: Car, tag: "In city" },
  { title: "Airport Pickup & Drop", desc: "Meet-and-greet at Jodhpur Airport with a name-board.", icon: PlaneTakeoff, tag: "Popular" },
  { title: "Railway Station Pickup", desc: "Timely pickups at Jodhpur Junction, 24×7.", icon: Train, tag: "Station" },
  { title: "Outstation Taxi", desc: "Comfort-first rides to Udaipur, Jaisalmer, Mount Abu & beyond.", icon: MapPinned, tag: "Long trip" },
  { title: "One-Way Taxi", desc: "Pay only for one side of the journey. Great value.", icon: ArrowRight, tag: "Save 40%" },
  { title: "Round-Trip Taxi", desc: "Return trips with waiting & sightseeing time built-in.", icon: Route, tag: "Flexible" },
  { title: "Corporate Cab", desc: "Monthly billing, employee pickups, GST invoicing.", icon: Briefcase, tag: "B2B" },
  { title: "Wedding Cab", desc: "Decorated cars & fleet packages for the big day.", icon: HeartHandshake, tag: "Baraat" },
  { title: "Tempo Traveller", desc: "12/17-seater A/C tempos for families & groups.", icon: Bus, tag: "Group" },
  { title: "Luxury Car Rental", desc: "Chauffeured premium sedans & SUVs on demand.", icon: Crown, tag: "Premium" },
  { title: "Rajasthan Tour Packages", desc: "3, 5, 7 & 10-day heritage circuits — curated by locals.", icon: Compass, tag: "Signature" },
];

export default function Services() {
  return (
    <section id="services" className="relative bg-white text-brand-ink py-24 md:py-32" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">Chapter II · What we do</div>
            <h2 className="mt-4 font-display font-black text-5xl md:text-6xl leading-[1.02]">
              Every ride, <span className="italic text-brand-orange font-medium">crafted.</span>
            </h2>
          </div>
          <p className="max-w-md text-brand-ink/65">Eleven services under one roof — from a 5-minute city ride to a ten-day Rajasthan odyssey.</p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {SERVICES.map((s, i) => (
            <motion.a
              key={s.title}
              href={whatsappLink(`Hi, I'd like to book — ${s.title}.`)}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
              className="group relative overflow-hidden rounded-3xl border border-brand-ink/8 bg-brand-cream/40 p-6 hover:bg-brand-blue hover:text-white transition-colors"
              data-testid={`service-${i}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-white group-hover:bg-brand-orange text-brand-blue group-hover:text-white flex items-center justify-center transition-colors">
                  <s.icon className="w-5 h-5" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-blue group-hover:text-brand-orange">{s.tag}</span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold leading-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-brand-ink/65 group-hover:text-white/80 leading-relaxed">{s.desc}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-blue group-hover:text-brand-orange">
                Book on WhatsApp <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
